import React, { useEffect, useRef, useState } from "react";
import CompanyHeader from "./CompanyHeader";
import FooterLinks from "../LandingPage/FooterLinks";
import SeoMeta from "../SEO/SeoMeta";
import M2TInterlinkDiagram from "../../components/common/M2TInterlinkDiagram";
import { Helmet } from "react-helmet";

const MARKETS = [
  "Forex Market",
  "Stock Market",
  "Index Market",
  "Commodity Market",
  "Crypto Market",
  "Options & Derivatives",
  "Futures Market",
  "Binary Options",
  "Mutual Funds",
  "ETFs",
  "Bonds & Fixed Income",
  "REITs / Real Assets",
  "PAMM / MAM Accounts",
  "Angel / VC / Private Equity",
  "Algorithmic / Quant Markets",
  "DeFi & Web3 Finance",
  "Index Investing",
  "Corporate Markets",
  "Sovereign & Central Bank Impact",
  "Carbon Credit & ESG Markets",
  "Insurance & Risk Markets",
  "Microfinance & P2P Lending",
  "Crowdfunding & Tokenized Assets",
  "International Trade Finance & FX Hedging",
];

const CATEGORIES = [
  "Traders",
  "Signal Providers",
  "Strategy Providers",
  "Coders",
  "Automation Experts",
  "Educators",
  "Mentors",
  "Analysts",
  "Finance & Accounting Professionals",
  "Researchers",
  "Investors",
  "Influencers",
  "Promoters",
  "Brokers",
  "Prop Firms",
  "Platform Providers",
  "Compliance Professionals",
  "Legal Professionals",
  "Tax Professionals",
  "Content Creators",
  "Community Moderators",
  "Media Advertisers",
  "Investment Service Providers",
  "Capital Fund Providers",
  "Career Services",
  "Mutual Fund & SIP Promoters",
  "Carbon Credit & ESG Markets",
  "Insurance & Risk Markets",
  "Crowdfunding, P2P & Trade Finance",
];

export default function Solutions() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    function updateHeight() {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-white text-black">
      <SeoMeta
        title="Million2Trillion Solutions — Wide Coverage for 24 Markets, 29+ Categories, 200+ Roles"
        description="Explore how M2T empowers every market participant with unique solutions, scam-free protections, and seamless interlinks across 24 global markets, 29+ categories, and over 200 verified user roles. M2T is the world's first platform where every user is both a buyer and a seller, with zero fraud and real value exchange."
        url="https://million2trillion.com/solutions"
        canonical="https://million2trillion.com/solutions"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Million2Trillion Solutions",
            description:
              "M2T’s solutions for every market role: 24 global markets, 29+ categories, 200+ verified user roles. Explore trusted, AI-powered features and discover the ecosystem for trading, learning, earning, and launching in global finance.",
            url: "https://million2trillion.com/solutions",
            mainEntity: [
              {
                "@type": "ItemList",
                name: "Global Markets",
                itemListElement: MARKETS.map((market, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: market,
                })),
              },
              {
                "@type": "ItemList",
                name: "Market Categories",
                itemListElement: CATEGORIES.map((cat, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: cat,
                })),
              },
            ],
            publisher: {
              "@type": "Organization",
              name: "Million2Trillion",
              url: "https://million2trillion.com",
              logo: {
                "@type": "ImageObject",
                url: "https://million2trillion.com/assets/images/logo.png",
              },
            },
          })}
        </script>
      </Helmet>

      <CompanyHeader ref={headerRef} activePage="solutions" />
      <div style={{ height: headerHeight }} aria-hidden="true" />
      {/* --- BANNER IMAGE --- */}
      <div className="w-full bg-white">
        <img
          src="/assets/company/Million2Trillion.gif"
          alt="About Million2Trillion"
          className="block w-full"
          style={{
            width: "100vw",
            maxWidth: "100vw",
            height: "auto",
          }}
          draggable={false}
        />
      </div>

      <main className="relative z-10 mx-auto max-w-7xl space-y-16 px-6 py-16 md:px-20">
        {/* Hero: Coverage, Value, Unique Model */}
        <header className="mx-auto mb-10 max-w-4xl">
          <div className="mt-16 rounded-xl border-l-4 border-purple-600 bg-gradient-to-br from-gray-100 via-gray-50 to-white p-8 shadow-lg">
            <h1 className="mb-5 text-center text-3xl font-extrabold text-black md:text-4xl">
              Global Solutions. Every Market. Every Role.
            </h1>
            <p className="text-center text-lg font-normal text-gray-800 md:text-xl">
              <b>Million2Trillion (M2T)</b> is the complete financial market
              ecosystem, powered by AI, designed to support every corner of the
              market. Built for traders, coders, educators, mentors, analysts,
              investors, firms, and more, M2T empowers every user—no matter
              their role or experience—to grow their career, build new products,
              collaborate globally, and launch their business.
              <br />
              <br />
              We’re entering a new era of the market community:{" "}
              <span className="font-semibold text-purple-700">
                one platform with wide coverage of 24 global markets, 29+
                professional categories, and over 200 verified roles.
              </span>
              Here, every user is both a buyer and a seller, earning simply by
              providing value and protected by industry-leading{" "}
              <span className="font-semibold text-green-600">
                anti-scam, anti-fraud, and anti-fake ID safeguards
              </span>
              .
              <br />
              <br />
              M2T unites everyone in finance under one trusted hub, from chart
              to funding—removing barriers, reducing risk, and enabling instant
              collaboration. Whether you want to trade, teach, learn, create,
              fund, or innovate, M2T offers seamless, real-time tools and
              dedicated business protections, including our{" "}
              <span className="font-semibold">
                7-day buyer protection policy
              </span>{" "}
              and{" "}
              <span className="font-semibold">
                SmartCard-verified onboarding
              </span>
              .
              <br />
              <br />
              <span className="font-semibold">
                This is the next generation of the financial market
                network—intelligent, secure, and truly collaborative—where your
                unique value is recognized and every market opportunity is
                within reach.
              </span>
            </p>
          </div>
        </header>

        {/* 24 Markets */}
        <section className="mx-auto max-w-5xl py-6 text-center">
          <h2 className="mb-3 text-2xl font-bold text-purple-600 md:text-3xl">
            24 Global Markets Supported
          </h2>
          <div className="grid grid-cols-2 gap-4 text-base font-semibold text-gray-800 sm:grid-cols-3 md:grid-cols-4">
            {MARKETS.map((market) => (
              <span key={market} className="rounded-lg bg-gray-100 p-2">
                {market}
              </span>
            ))}
          </div>
        </section>

        {/* 29 Categories */}
        <section className="mx-auto max-w-5xl py-6 text-center">
          <h2 className="mb-3 text-2xl font-bold text-blue-700 md:text-3xl">
            29+ Market Categories
          </h2>
          <div className="grid grid-cols-2 gap-4 text-base font-medium text-gray-700 sm:grid-cols-3 md:grid-cols-4">
            {CATEGORIES.map((category) => (
              <span
                key={category}
                className="rounded-lg border border-purple-100 bg-gray-50 p-2"
              >
                {category}
              </span>
            ))}
          </div>
        </section>

        {/* SEO Story: Interlinking, Value, Protection */}
        <section className="mx-auto max-w-5xl space-y-8 py-10 text-lg leading-relaxed text-gray-800">
          <h2 className="mb-4 text-2xl font-bold text-black md:text-3xl">
            How M2T Empowers & Interlinks Every User
          </h2>
          <p>
            At Million2Trillion, we believe that financial empowerment starts
            with real identity, verified trust, and seamless collaboration. Our
            ecosystem uniquely connects <b>over 200 roles</b>—from traders and
            coders, to educators, analysts, and capital providers—across all
            market types.
          </p>
          <p>
            Our <b>interlink matrix</b> ensures every role gives and gets value:
            signal providers monetize performance, coders deploy automation,
            mentors guide traders, analysts validate strategies, and investors
            find verified talent. All value flows are two-way, and every
            participant can be a buyer, seller, or both—removing the old silos
            of traditional finance and enabling a new era of cross-role
            opportunity.
          </p>
          <p>
            M2T is the{" "}
            <b>
              first financial marketplace to unite the full market lifecycle
            </b>{" "}
            on one platform. From your first chart analysis, through trading,
            learning, earning, and finally to launching your own fund, product,
            or career—M2T supports every step with built-in compliance,
            community safety, instant payments, and the ability to scale up your
            brand.
          </p>
          <p>
            Our <b>6 central hubs</b> (Connect, Trade, Learn, Earn, Build,
            Launch) deliver specialized tools and marketplaces for every user
            type. Each solution is backed by SmartCard identity, real-time
            KYC/AML monitoring, and a 7-day money protection policy to defend
            buyers and sellers from scams, fraud, and false schemes.
          </p>
          <p>
            Whether you’re launching a trading strategy, raising capital for
            your startup, delivering educational content, managing a prop firm,
            building bots, or running a community,{" "}
            <b>
              M2T is the central business hub to exchange and grow your unique
              value
            </b>
            .
          </p>
        </section>

        <M2TInterlinkDiagram />

        {/* 200+ Roles: Show as SEO text for long-tail */}
        <section className="mx-auto max-w-5xl py-10">
          <h2 className="mb-4 text-center text-2xl font-bold text-green-600 md:text-3xl">
            200+ Verified Roles—All Welcome, All Connected
          </h2>
          <div
            className="mx-auto max-w-4xl space-y-2 text-xs leading-7 text-gray-600 md:text-base"
            style={{ columnCount: 2, columnGap: 32 }}
          >
            {/* 
              For best SEO, this can be even longer: 
              If you want ALL role names from your doc here, just paste the full list inside this div
              Or map from a ROLE_NAMES array if you want dynamic.
            */}
            {ROLE_NAMES.map((role) => (
              <div key={role} className="mb-1">
                {role}
              </div>
            ))}
          </div>
        </section>

        {/* Interlink Matrix Story */}
        <section className="mx-auto max-w-5xl space-y-6 py-10 text-gray-800">
          <h2 className="text-xl font-bold text-purple-600 md:text-2xl">
            How Every Category Interlinks on M2T
          </h2>
          <p>
            M2T’s interlink matrix powers a true “market of markets”—where
            traders fuel the ecosystem, signal and strategy providers sell
            performance, coders and automation experts build the infrastructure,
            and educators, mentors, and analysts deliver verified knowledge.
            Influencers, promoters, brokers, funds, compliance, legal and tax
            professionals, content creators, and media advertisers all have
            direct, protected channels to deliver value and earn.
          </p>
          <p>
            Every user role is cross-linked: for example, traders access
            signals, bots, research, and mentors; signal providers gain audience
            and data from traders, and monetize via subscriptions; coders serve
            traders, brokers, automation, and prop firms; every buyer can also
            become a seller, and vice versa.
          </p>
          <p>
            By mapping, protecting, and rewarding every value flow—M2T delivers
            the only all-in-one, scam-free, global financial platform for every
            role and market.
          </p>
        </section>

        <section className="mx-auto mb-12 mt-14 max-w-4xl px-4">
          <h2 className="mb-5 text-center text-2xl font-bold text-black md:text-3xl">
            High Trustable System – Empowering Every User With Advanced Security
            & Smart Collaboration
          </h2>
          <div className="rounded-xl border-l-4 border-yellow-400 bg-gradient-to-br from-yellow-50 via-white to-gray-100 p-6 text-lg font-normal leading-relaxed text-gray-900 shadow-lg">
            <p className="mb-4">
              <b>Million2Trillion (M2T)</b> is built for total trust and
              transparency. Our platform features{" "}
              <span className="font-semibold text-black">
                bank-grade security, in-depth KYC with SmartCard-verified
                onboarding,
              </span>{" "}
              and a dynamic SmartCard control system for real-time user
              management.
            </p>
            <p className="mb-4">
              Every transaction, booking, and connection is protected by a{" "}
              <span className="font-semibold text-black">
                Zero Scam Guarantee
              </span>{" "}
              and proactive anti-fraud monitoring. With our{" "}
              <span className="font-semibold text-black">
                inbuilt affiliate and offer system
              </span>
              , every user and every role automatically helps promote and grow
              other businesses across the network—creating true network effects
              that multiply opportunity for all.
            </p>
            <p className="mb-4">
              M2T gives you a{" "}
              <span className="font-semibold text-black">
                personalized AI assistant
              </span>{" "}
              for career and business growth, plus built-in tools for{" "}
              <span className="font-semibold text-black">
                instant messaging, team management, secure file storage,
                multilingual support, and automated payments
              </span>
              .
            </p>
            <p>
              From{" "}
              <span className="font-semibold text-black">
                integrated wallet services
              </span>{" "}
              and split-screen productivity mode to an advanced{" "}
              <span className="font-semibold text-black">
                AI-powered insights engine
              </span>
              , M2T is the ultimate business hub where your identity, value, and
              security always come first.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="space-y-4 pt-10 text-center">
          <a
            href="/waitlist"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-block transition-transform hover:scale-105"
            style={{ textDecoration: "none" }}
          >
            <h2 className="text-2xl font-bold text-blue-900 underline hover:text-blue-700 group-hover:underline">
              Join Million2Trillion (M2T) — The Only Platform Empowering Every
              Market Role
            </h2>
          </a>
        </section>
      </main>

      <footer className="mt-36 border-t border-gray-200 bg-white">
        <FooterLinks />
      </footer>
    </div>
  );
}

// Add ROLE_NAMES as a flat array (for SEO). Here's an example; fill with your full list for SEO power:
const ROLE_NAMES = [
  "Retail Forex Trader",
  "Retail Stock Trader",
  "Retail Crypto Trader",
  "Index Futures Trader",
  "Commodity Futures Trader",
  "Binary Options Trader",
  "Funded Prop Trader",
  "Intraday Trader",
  "Scalper",
  "Swing Trader",
  "Position Trader",
  "Copy Trader",
  "Demo Account Competitor",
  "Forex Signal Provider",
  "Stock Signal Provider",
  "Crypto Signal Provider",
  "Index Signal Provider",
  "Commodity Signal Provider",
  "Binary Signal Provider",
  "Options Strategy Provider",
  "Scalping Strategy Creator",
  "Swing Setup Publisher",
  "Chart Pattern Specialist",
  "Breakout/Trendline Analyst",
  "Indicator Setup Curator",
  "Live Strategy Session Host",
  "EA Developer (MT4/MT5)",
  "Crypto Bot Developer",
  "Pine Script Coder",
  "Custom Indicator Developer",
  "Risk Management Tool Coder",
  "Signal Copy Bot Developer",
  "Backtesting Expert",
  "TradingView Alert Integrator",
  "Automation Workflow Builder",
  "Telegram Bot Creator",
  "Portfolio Tracker Developer",
  "Trading UI/UX Designer",
  "Forex Educator",
  "Stock Market Educator",
  "Crypto Educator",
  "Options Strategy Trainer",
  "Technical Analysis Educator",
  "Backtesting Coach",
  "Trading Psychology Coach",
  "Personal Finance Coach",
  "Risk Management Mentor",
  "Scalping Mentor",
  "Swing Trading Mentor",
  "Algo Trading Instructor",
  "Live Room Mentor",
  "Technical Analyst",
  "Fundamental Analyst",
  "Tokenomics Analyst",
  "Economic News Analyst",
  "Options Data Analyst",
  "Volume Profile Analyst",
  "Candlestick Analyst",
  "On-chain Analyst",
  "Quant Analyst",
  "Chartered Accountant (CA)",
  "Certified Public Accountant (CPA)",
  "Tax Filing Specialist",
  "Financial Controller",
  "Business Accountant",
  "Bookkeeper (Manual/Digital)",
  "GST/VAT Filing Expert",
  "Audit & Assurance Consultant",
  "CFO-as-a-Service Provider",
  "Payroll & Compliance Expert",
  "Investment Fund Accountant",
  "Portfolio Bookkeeping Expert",
  "Regulatory Reporting Specialist",
  "Portfolio Researcher",
  "Backtest Reviewer",
  "Sentiment Researcher",
  "Chart Breakdown Writer",
  "Event-based Strategy Researcher",
  "Retail Investor",
  "Long-Term Holder",
  "Angel Investor",
  "Portfolio Manager",
  "Mutual Fund Allocator",
  "Index Investor",
  "Copy Investment Allocator",
  "Passive Income Investor",
  "HNI / PAMM / MAM Investor",
  "Strategy Capital Funder",
  "Forex Influencer",
  "Crypto Influencer",
  "YouTube Educator",
  "Twitter Financial Creator",
  "Reels Creator",
  "Meme Page Finfluencer",
  "Affiliate Marketer",
  "Reviewer (Courses, Signals)",
  "Podcast Host",
  "Event Host/Speaker",
  "Resume Builder Promoter",
  "Broker Representative",
  "White-Label Broker Provider",
  "Prop Firm Owner",
  "Affiliate Manager",
  "Challenge Manager",
  "Lead Verifier",
  "Signal Copy Platform Admin",
  "MT4/MT5 Server Operator",
  "Brokerage Infra Consultant",
  "KYC/AML Officer",
  "Prop Compliance Manager",
  "Regulation Policy Enforcer",
  "Fund Validation Officer",
  "Platform Ethics Consultant",
  "Background Screening Specialist",
  "Trading Legal Advisor",
  "IP Protection Advisor",
  "Smart Contract Lawyer",
  "Cross-border Legal Consultant",
  "User Dispute Mediator",
  "Legal Audit Advisor",
  "Forex/Crypto Tax Advisor",
  "Global Tax Planner",
  "Options Tax Analyst",
  "Digital Income Specialist",
  "Filing Assistant for Traders",
  "Blog & Article Writer",
  "Newsletter Curator",
  "Visual/Infographics Designer",
  "Figma UI Designer",
  "Wiki / Knowledgebase Builder",
  "Meme Creator",
  "Telegram/Discord Manager",
  "Forum Moderator",
  "Feedback Collector",
  "Job Discussion Moderator",
  "YouTube/Instagram Creator",
  "Podcast Host",
  "Sponsored Job Marketer",
  "Meme Page Advertiser",
  "SEBI RIA / PMS Operator",
  "Robo-Advisory Provider",
  "Financial Planning App Builder",
  "Investment Suitability Analyst",
  "VC Partner",
  "Seed Fund Manager",
  "Institutional Capital Rep",
  "Grant / Program Creator",
  "Investment Banker / M&A Leader",
  "Resume Builder",
  "Job Platform Owner",
  "Mock Interview Coach",
  "Fintech Job Headhunter",
  "Certified MF Distributor",
  "SIP Educator",
  "AMC Representative",
  "Rebalancing Expert",
  "ESG Analyst",
  "Carbon Credit Broker",
  "Sustainability Consultant",
  "Net Zero Expert",
  "Insurance Advisor",
  "Risk Analyst",
  "Actuary",
  "Reinsurance Specialist",
  "InsurTech Platform Builder",
  "Crowdfunding Advisor",
  "Tokenization Consultant",
  "P2P Lending Operator",
  "Microfinance Planner",
  "FX Hedging Advisor",
  "Trade Finance Expert",
];
